import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";

import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);

export const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY = 1000;
const MAX_DELAY = 30000;

type RetryConfig = InternalAxiosRequestConfig & {
  _retryCount?: number;
  _maxRetries?: number;
};

/**
 * Calculates exponential backoff delay with jitter
 * @param retryCount - Current retry attempt (0-indexed)
 * @returns Delay in milliseconds
 */
const calculateDelay = (retryCount: number): number => {
  const exponentialDelay = DEFAULT_BASE_DELAY * Math.pow(2, retryCount);
  const jitter = Math.random() * 0.3 * exponentialDelay; // 0-30% jitter

  return Math.min(exponentialDelay + jitter, MAX_DELAY);
};

/**
 * Gets the delay from Retry-After response header.
 * Supports both delay-seconds (integer) and HTTP-date (RFC 5322) formats.
 * @param retryAfterHeader - Value of Retry-After header
 * @returns Delay in milliseconds, or null if invalid or in the past
 */
const getRetryAfterDelay = (
  retryAfterHeader: string | undefined,
): number | null => {
  if (isFalsy(retryAfterHeader)) {
    return null;
  }

  const trimmed = String(retryAfterHeader).trim();

  const delaySeconds = Number.parseInt(trimmed, 10);

  if (!Number.isNaN(delaySeconds) && delaySeconds > 0) {
    return Math.min(delaySeconds * 1000, MAX_DELAY);
  }

  const targetDate = Date.parse(trimmed);

  if (Number.isNaN(targetDate)) {
    return null;
  }

  const delay = targetDate - Date.now();

  if (delay <= 0) {
    return null;
  }

  return Math.min(delay, MAX_DELAY);
};

/**
 * Creates a retry interceptor that uses the given axios instance for retries,
 * so retried requests go through all instance interceptors (auth, error, etc.).
 */
export const createRetryInterceptor = (instance: AxiosInstance) => {
  const retryInterceptor = async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;

    if (isFalsy(config)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;

    // Include responses without status code (e.g. network errors) for retry
    const isRetryable =
      error.response === undefined ||
      (isTruthy(status) && RETRYABLE_STATUS_CODES.has(status));

    if (!isRetryable) {
      return Promise.reject(error);
    }

    const maxRetriesHeader = config.headers["max-retries"];
    const maxRetries = isTruthy(maxRetriesHeader)
      ? Number.parseInt(String(maxRetriesHeader), 10)
      : DEFAULT_MAX_RETRIES;

    if (Number.isNaN(maxRetries) || maxRetries < 0) {
      return Promise.reject(error);
    }

    const currentRetryCount = config._retryCount ?? 0;

    if (currentRetryCount >= maxRetries) {
      return Promise.reject(error);
    }

    config._retryCount = currentRetryCount + 1;
    config._maxRetries = maxRetries;

    const retryAfterDelay =
      error.response !== undefined
        ? getRetryAfterDelay(error.response.headers["retry-after"])
        : null;
    const delay = retryAfterDelay ?? calculateDelay(currentRetryCount);

    await new Promise((resolve) => {
      setTimeout(resolve, delay);
    });

    return instance(config);
  };

  return retryInterceptor;
};
